import { FC } from "react";

import { getPageGridClass } from "./grid";

import { AssessmentStatus } from "api/api-data-types";
import { INDEX_NOT_FOUND } from "components/constants";
import { DueDateButton } from "components/materials/page/DueDateButton";
import { InteractionModeMenu } from "components/materials/page/interaction-mode/InteractionModeMenu";
import { PointsConfig } from "components/materials/page/outcomes/PointsConfig";
import { InteractionMode, PageType, SectionKind } from "components/server-types";
import { t } from "i18n/i18n";
import { ToggleSwitch } from "mds/components/ToggleSwitch";
import { Tooltip } from "mds/components/Tooltip";
import { LinkButton } from "mds/components/buttons/LinkButton";
import { useAppSelector } from "store/index";
import { selectCurrentSectionId, selectNestedPageOutcomes } from "store/selectors";
import { selectAssessmentInfoForPage } from "store/selectors/computed";
import { changePageInteractionMode, usePageActions } from "utils/page";
import { isSharedClassPage } from "utils/page-utils";
import { isLinkedSection, isTemplateSection } from "utils/publishing";
import { pageUrl } from "utils/urls";

const getPointsConfigTooltip = ({
  canConfigurePoints,
  isShared,
  hasAssessments,
  hasOutcomes,
  isInLinkedSection,
}: {
  canConfigurePoints: boolean;
  isShared: boolean;
  hasAssessments: boolean;
  hasOutcomes: boolean;
  isInLinkedSection: boolean;
}): string => {
  if (isInLinkedSection) {
    return t("manage_section_pages_modal.linked_section_points_tooltip");
  }

  if (canConfigurePoints) {
    return t("tooltip.assessment_points");
  }

  if (isShared) {
    return t("tooltip.points_config_shared_class");
  }

  if (hasAssessments) {
    return t("tooltip.points_config_has_assessments");
  }

  if (!hasOutcomes) {
    return t("page_overview_header.disabled_info");
  }

  return t("manage_section_pages_modal.points_disabled_tooltip");
};

export const PageRow: FC<{
  page: PageType;
  sectionKind?: SectionKind;
  onPageClick: (href: string) => void;
}> = ({ page, sectionKind, onPageClick }) => {
  const { savePage } = usePageActions();
  const sectionId = useAppSelector(selectCurrentSectionId);
  const pageOutcomes = useAppSelector((s) => selectNestedPageOutcomes(s, page.id));
  const assessmentStatusInfo = useAppSelector((s) => selectAssessmentInfoForPage(s, page.id));
  const hasAssessments = (assessmentStatusInfo?.assessed_submissions_count ?? 0) > 0;
  const hasSubmissions = [AssessmentStatus.ASSESSABLE, AssessmentStatus.ASSESSED].includes(
    assessmentStatusInfo?.status,
  );

  const isShared = isSharedClassPage(page);
  const hasOutcomes = pageOutcomes.length > 0;
  const isInLinkedSection = isLinkedSection(sectionKind);
  const isInTemplateSection = isTemplateSection(sectionKind);
  const canConfigurePoints = !isInLinkedSection && hasOutcomes && !isShared && !hasAssessments;

  const onChangeInteractionMode = (newInteractionMode: InteractionMode) => {
    changePageInteractionMode({
      page,
      newInteractionMode,
      isInTemplateSection,
    });
  };

  const isReleased = !!page.released_at;
  const isLocked = !!page.locked_at;
  const href = sectionId ? pageUrl(sectionId, page.id) : "";

  return (
    <div
      className={`${getPageGridClass(isInTemplateSection)} border-b border-black-tint-90 py-1.5 text-xs`}
      role="row"
    >
      <div className="min-w-0" role="cell">
        <LinkButton size="xs" title={page.title} onClick={() => onPageClick(href)}>
          <span className="min-w-0 truncate">{page.title}</span>
        </LinkButton>
      </div>

      <div className="min-w-0" role="cell">
        <InteractionModeMenu
          hasSubmissions={hasSubmissions}
          page={page}
          pageGroupIndex={INDEX_NOT_FOUND}
          readOnly={isInLinkedSection}
          readOnlyTooltip={
            isInLinkedSection
              ? t("manage_section_pages_modal.linked_section_page_type_tooltip")
              : undefined
          }
          showDisabledChips
          showNormalGroupButton
          onAuthorClick={onChangeInteractionMode}
        />
      </div>

      <div className="min-w-0" role="cell">
        <PointsConfig
          disabled={!canConfigurePoints}
          page={page}
          title={getPointsConfigTooltip({
            canConfigurePoints,
            isShared,
            hasAssessments,
            hasOutcomes,
            isInLinkedSection,
          })}
          onSave={(points) => savePage({ id: page.id, max_points: points })}
        />
      </div>

      {!isInTemplateSection && (
        <>
          <div className="min-w-0" role="cell">
            <DueDateButton page={page} shortText />
          </div>

          <div role="cell">
            <Tooltip
              text={
                isLocked
                  ? t("manage_section_pages_modal.unlock_page")
                  : t("manage_section_pages_modal.lock_page")
              }
            >
              <ToggleSwitch
                eventName="Manage content modal - Toggle lock"
                eventProps={{ pageId: page.id, pageTitle: page.title }}
                isSelected={isLocked}
                onClick={() =>
                  savePage({
                    id: page.id,
                    locked_at: isLocked ? null : new Date().toISOString(),
                  })
                }
              />
            </Tooltip>
          </div>

          <div role="cell">
            <Tooltip
              text={
                isReleased
                  ? t("manage_section_pages_modal.unrelease_page")
                  : t("manage_section_pages_modal.release_page")
              }
            >
              <ToggleSwitch
                eventName="Manage content modal - Toggle release"
                eventProps={{ pageId: page.id, pageTitle: page.title }}
                isSelected={isReleased}
                onClick={() =>
                  savePage({
                    id: page.id,
                    released_at: isReleased ? null : new Date().toJSON(),
                  })
                }
              />
            </Tooltip>
          </div>
        </>
      )}
    </div>
  );
};
